import type { LegalEntity } from '@/model/LegalEntity';
import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

type AvailablePermission =
  | 'canAccess' // permission "SGM_COCKPIT" : Basic grant, allows to see the main root tabs (ptf summary, detailed valuation, operations, alerts, itv, doc, fee, warranties, authorisations).
  | 'canSeeMyDesk' // Allows users to see their desk on the landing page. If user also have "CanSeeMyPortfolio", the frontend displays a toggle between the two modes.
  | 'canSeeMyPortfolio' // Allows users to see their portfolio on the landing page. If the user also has "CanSeeMyDesk", the frontend displays a toggle button between the two modes.
  | 'canSeePortfolioTabOnHomepage' // Allows users to see the portfolio tab on the homepage which displays the roots of the user (for FIM : all the roots s·he can access).
  | 'canSeeOstTabOnHomepage' // Allows users to see the OST tab on the homepage
  | 'canSeeCashTransfersOnHomepage' // Can see cash transfers tab on landing page. Does not grant the possibility to modify or validate cash transfers.
  | 'canSeeCashSearchOnHomepage' // Can see cash search tab on landing page. Does not grant the possibility to modify or validate cash transfers.
  | 'canSeeDebitAccountsOnHomepage' // Allows users to see debtor accounts on the homepage
  | 'canSearchRoots' // Allows to see and use the search bar (top screen) in "root" mode.
  | 'canSearchPersons' // Allows to see and use the search bar (top screen) in "person" mode.
  | 'canSeeApplicationsLinks' // Allows to use multiple app links (top left : main list + buttons dispatched in root tabs). Does not include the MAKS links & buttons as they are controlled by "CanDoMaksOperations".
  | 'canSeeCashTransfers' // Can see cash transfert tab on root and "visualize" button on cash transfers. Does not grant the possibility to modify or validate cash transfers.
  | 'canDoCashTransfers' // Allows users to execute, edit, cancel, validate cash transfers. Hence controls several buttons in the app associated to these actions.
  | 'canDoMaksOperations' // Allows to use maks operation from detailed valuation and from top right corner button
  | 'canSeeRestrictedData' // Identifies non FIM users (internal users can see some data which are hidden to FIMs)
  | 'canDoDocumentGeneration' // Generate documents : RIB and Portfolio Statements
  | 'canSeeAsmLink' // Allows to see and click to Asm link in Alert tab
  | 'cashTransferAudit' // Allows to see cash transfers audit screen (only for support users) 
  | 'canDoArrayExport' // Allows to export data from AgGrids as CSV or Excel
  | 'canSeeFutureLiquidities' // in tab 'future operations' : cash flows to occur in future
  | 'canSeeBookOrder' // in tab 'future operations' : book order
  | 'canSeeAlertTabOnRoot' // alerts tab on root
  | 'isBranch' // indicates if is a member of a branch (ITA or BEL) // WARNING : deprecated, replaced by hasLimitedRootAccess
  | 'canSeePositionDetails' // Allows users to see the operation details drawer by clicking the isin cell in detailed valuation tab
  | 'canSeeSigmaOperations' // Allows users to see, in future operations tab, the operations from Sigma array
  | 'canSeeInputDocuments' // Allows users to see documents provided by clients such as ID cards, passports, etc.
  | 'canSeeOutputDocuments' // Allows users to see documents given to clients such as mensual performance reports 
  | 'canSeeFeesTab' // Allows users to see the Fees tab

type UserState = {
  permissions: Record<AvailablePermission, boolean>;
  userEntities: LegalEntity[]; // country/division the user is a part of (ex : ['LUX', 'MON'] in rare cases, most probably just ['LUX'] or ['MON']), should never be empty
  selectedEntity: LegalEntity; // currently selected legal entity, only relevant for users allowed to see several entities
};

const initialState: UserState = {
  userEntities: [],
  selectedEntity: 'LUX',
  permissions: {
    canAccess: false,
    canSeeMyDesk: false,
    canSeeMyPortfolio: false,
    canSeeRestrictedData: false,
    canSeePortfolioTabOnHomepage: false,
    canSeeOstTabOnHomepage: false,
    canSeeCashTransfersOnHomepage: false,
    canSeeCashSearchOnHomepage: false,
    canSeeDebitAccountsOnHomepage: false,
    canSearchRoots: false,
    canSearchPersons: false,
    canSeeApplicationsLinks: false,
    canSeeCashTransfers: false,
    canDoCashTransfers: false,
    canDoMaksOperations: false,
    canDoDocumentGeneration: false,
    canSeeAsmLink: false,
    cashTransferAudit: false,
    canDoArrayExport: false,
    canSeeFutureLiquidities: false,
    canSeeBookOrder: false,
    canSeeAlertTabOnRoot: false,
    isBranch: false,
    canSeePositionDetails: false,
    canSeeSigmaOperations: false,
    canSeeInputDocuments: false,
    canSeeOutputDocuments: false,
    canSeeFeesTab: false,
  },
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUserEntities: (state: UserState, action: PayloadAction<LegalEntity[]>) => {
      state.userEntities = action.payload;
    },
    setSelectedEntity: (state: UserState, action: PayloadAction<LegalEntity>) => {
      if(hasAccessToEntity({user:state}, action.payload)) {
        state.selectedEntity = action.payload
      } else console.log("User has not access to selected entity", action.payload)
    },
    setUserPermissions: (
      state: UserState,
      action: PayloadAction<Record<AvailablePermission, boolean>>,
    ) => {
      state.permissions = action.payload;
    },
  },
  selectors: {
    canAccess: (state) => state.permissions.canAccess, // permission "SGM_COCKPIT"
    canSeeMyDesk: (state) => state.permissions.canSeeMyDesk,
    canSeeMyPortfolio: (state) => state.permissions.canSeeMyPortfolio,

    canSeePortfolioTabOnHomepage: (state) => state.permissions.canSeePortfolioTabOnHomepage,
    canSeeOstTabOnHomepage: (state) => state.permissions.canSeeOstTabOnHomepage,
    canSeeCashTransfersOnHomepage: (state) => state.permissions.canSeeCashTransfersOnHomepage,
    canSeeCashSearchOnHomepage: (state) => state.permissions.canSeeCashSearchOnHomepage,
    canSeeDebitAccountsOnHomepage: (state) => state.permissions.canSeeDebitAccountsOnHomepage,

    canSearchRoots: (state) => state.permissions.canSearchRoots,
    canSearchPersons: (state) => state.permissions.canSearchPersons,
    canSeeApplicationsLinks: (state) => state.permissions.canSeeApplicationsLinks,

    canSeeRestrictedData: (state) => state.permissions.canSeeRestrictedData,
    canSeeCashTransfers: (state) => state.permissions.canSeeCashTransfers,
    canDoCashTransfers: (state) => state.permissions.canDoCashTransfers,
    canDoMaksOperations: (state) => state.permissions.canDoMaksOperations,
    canSeeAsmLink: (state) => state.permissions.canSeeAsmLink,
    canDoDocumentGeneration: (state) => state.permissions.canDoDocumentGeneration,

    cashTransferAudit: (state) => state.permissions.cashTransferAudit,
    canDoArrayExport: (state) => state.permissions.canDoArrayExport,
    canSeeFutureLiquidities: (state) => state.permissions.canSeeFutureLiquidities,
    canSeeSigmaOperations: (state) => state.permissions.canSeeSigmaOperations,
    canSeeBookOrder: (state) => state.permissions.canSeeBookOrder,
    canSeeAlertTabOnRoot: (state) => state.permissions.canSeeAlertTabOnRoot,
    isBranch: (state) => state.permissions.isBranch,
    canSeePositionDetails: (state) => state.permissions.canSeePositionDetails,
    canSeeOutputDocuments: (state) => state.permissions.canSeeOutputDocuments,
    canSeeInputDocuments: (state) => state.permissions.canSeeInputDocuments,
    canSeeFeesTab: (state) => state.permissions.canSeeFeesTab,

    hasAccessToEntity: (state, entity: LegalEntity) => state.userEntities.includes(entity),
    hasAccessToSeveralEntities: (state) => state.userEntities.length > 1,
    getSelectedEntity: (state) => state.selectedEntity,
    getRootEntityOrSelected: (state: UserState, rootId: string) : LegalEntity => {
      if(rootId && rootId.length === 8) {
        return getLegalEntityByRootId(rootId)
      }
      return getSelectedEntity({user: state})
    }
  },
});

const getLegalEntityByRootId = (rootId: string) : LegalEntity => {
  if(rootId.startsWith("70")) return 'LUX'
  if(rootId.startsWith("72")) return 'MCO'
  throw new Error("Could not find Legal Entity of root " + rootId)
}

export const { setUserPermissions, setUserEntities, setSelectedEntity } = userSlice.actions;

export const {
  canAccess,
  canSeeMyDesk,
  canSeeMyPortfolio,
  canSeePortfolioTabOnHomepage,
  canSeeOstTabOnHomepage,
  canSeeCashTransfersOnHomepage,
  canSeeCashSearchOnHomepage,
  canSeeDebitAccountsOnHomepage,
  canSearchRoots,
  canSearchPersons,
  canSeeApplicationsLinks,
  canSeeCashTransfers,
  canDoCashTransfers,
  canDoMaksOperations,
  canDoDocumentGeneration,
  hasAccessToEntity,
  hasAccessToSeveralEntities,
  getSelectedEntity,
  getRootEntityOrSelected,
  canSeeRestrictedData,
  canSeeAsmLink,
  cashTransferAudit,
  canDoArrayExport,
  canSeeFutureLiquidities,
  canSeeSigmaOperations,
  canSeeBookOrder,
  canSeeAlertTabOnRoot,
  isBranch,
  canSeePositionDetails,
  canSeeInputDocuments,
  canSeeOutputDocuments,
  canSeeFeesTab,
} = userSlice.selectors;
